import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ChannelService from "../../../services/KangarooServices/Intelligence/ChannelService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  Card,
  Col,
  Container,
  Row,
  Button,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  CardBody,
  CardImg,
} from "reactstrap";
import { imageBaseApi } from "../../../utilis/imageHelper";
import toast from "react-hot-toast";
import { DndContext, closestCenter } from "@dnd-kit/core";

import { SortableContext, useSortable, arrayMove } from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const SortableCard = ({ uni }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: uni.id });

  // Only apply transform from dnd-kit
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined, // leave transform to dnd-kit
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        className="h-100 text-center p-2 border"
        style={{
          border: isDragging
            ? "2px dashed #007bff"
            : isOver
            ? "2px solid #28a745"
            : "1px solid #ddd",
          backgroundColor: isDragging ? "#e7f1ff" : isOver ? "#e6ffe6" : "#fff",
          borderRadius: 12,
          boxShadow: isDragging
            ? "0 12px 24px rgba(0,0,0,0.25)"
            : "0 2px 5px rgba(0,0,0,0.1)",
          cursor: "grab",
          transition:
            "background-color 150ms ease, box-shadow 150ms ease, border 150ms ease",
        }}
      >
        <CardImg
          src={`${imageBaseApi}${uni.logo?.url}`}
          alt={uni.name}
          style={{ objectFit: "contain" }}
          className="mb-2 img-fluid"
        />
        <CardBody>
          <h6 className="text-truncate">{uni.name}</h6>
        </CardBody>
      </Card>
    </div>
  );
};

// ================= Main Component =================
const ReorderInstitute = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assignedUniversities, setAssignedUniversities] = useState([]);
  const [originalUniversities, setOriginalUniversities] = useState([]); // for cancel/reset
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAssignedUniversities();
  }, [id]);

  const fetchAssignedUniversities = async () => {
    setLoading(true);
    try {
      const response = await ChannelService.getdetails(id);
      setAssignedUniversities(response?.universities || []);
      setOriginalUniversities(response?.universities || []); // save original order
    } catch {
      toast.error("Failed to fetch universities");
    } finally {
      setLoading(false);
    }
  };

  const filteredUniversities = assignedUniversities.filter((u) =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Save reordered list
  const handleReorderSubmit = async () => {
    const payload = {
      universityIds: assignedUniversities.map((u) => u.id),
    };

    try {
      await ChannelService.reOrder(payload);
      toast.success("Universities reordered successfully!");
      navigate(`/channel-partners`);
    } catch {
      toast.error("Failed to reorder universities");
    }
  };

  // Cancel / Reset to original order
  const handleCancel = () => {
    setAssignedUniversities([...originalUniversities]);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Reorder Institutes"
          pageTitle="Channel Partner"
          breadcrumbItems={[
            { title: "Back to List", link: "/channel-partners" },
          ]}
        />

        <Row className="mb-3 align-items-center">
          <Col lg={4}>
            <Input
              placeholder="Search institutes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>

          <Col lg={4} className="d-flex gap-2">
            <Button color="success" onClick={handleReorderSubmit}>
              Save reorder
            </Button>
            <Button color="danger" onClick={handleCancel}>
              Reset
            </Button>
          </Col>
        </Row>

        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setAssignedUniversities((items) => {
              const oldIndex = items.findIndex((i) => i.id === active.id);
              const newIndex = items.findIndex((i) => i.id === over.id);
              return arrayMove(items, oldIndex, newIndex);
            });
          }}
        >
          <SortableContext items={filteredUniversities.map((i) => i.id)}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: "16px",
              }}
            >
              {filteredUniversities.map((uni) => (
                <SortableCard key={uni.id} uni={uni} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </Container>
    </div>
  );
};

export default ReorderInstitute;
